'use client';

import Grid from '@enlighten/design-system/dist/Grid';
import IconButton from '@enlighten/design-system/dist/IconButton';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import ChevronLeftSharp from '@mui/icons-material/ChevronLeftSharp';
import ChevronRightSharp from '@mui/icons-material/ChevronRightSharp';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';

import Logo from '@/assets/images/members/logo.png';
import { insightCategoryList } from '@/constants/insight';
import type { WpPost } from '@/types/wp';

export interface InsightCarouselProps {
	title: string;
	data: (Pick<WpPost, 'id' | 'categories'> & {
		title: { rendered: string };
		_embedded: {
			'wp:featuredmedia': { source_url: string }[];
		};
	})[];
}

export default function InsightCarousel({ title, data }: InsightCarouselProps) {
	const [index, setIndex] = useState<number>(0);

	const carouselRef = useRef<Slider>(null);

	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	useEffect(() => {
		carouselRef.current?.slickGoTo(index);
	}, [index]);

	return (
		<>
			<Grid container justifyContent="space-between" maxWidth={1440} margin="0 auto 27px" paddingX={3}>
				<Typography fontSize={{ xs: 22, md: 28 }} fontWeight={700}>
					{title}
				</Typography>
				<Grid
					sx={{
						'.MuiIconButton-root': {
							padding: 0,
							'&.Mui-disabled': {
								opacity: 0.3,
							},
							'.MuiSvgIcon-root': {
								color: 'common.black',
							},
						},
					}}
				>
					<IconButton
						title="prev"
						disabled={index === 0}
						onClick={() => {
							if (index > 0) {
								setIndex((state) => state - 1);
							}
						}}
					>
						<ChevronLeftSharp fontSize="large" />
					</IconButton>
					<IconButton
						title="next"
						disabled={index === data.length - 3}
						onClick={() => {
							if (index < data.length - 3) {
								setIndex((state) => state + 1);
							}
						}}
					>
						<ChevronRightSharp fontSize="large" />
					</IconButton>
				</Grid>
			</Grid>
			<Grid position="relative" left={{ xs: 0, md: 'max(calc(50% - 720px + 24px), 24px)' }} width={{ xs: '100%', md: 'calc((50% + 720px) * 1.1)' }}>
				<Slider ref={carouselRef} slidesToShow={isMobile ? 1 : 4} swipe={false} infinite={false} arrows={false}>
					{data.map((item, i) => {
						const filteredList = insightCategoryList.filter(({ value }) => item.categories.includes(value));

						return (
							<Grid key={i} minHeight={400} marginLeft={{ xs: '12px', md: 0 }} paddingRight={3} sx={{ outline: 'none', aspectRatio: '1 / 1' }}>
								<Link href={`/insight/${filteredList[0].category}/${item.id}`} title={item.title.rendered}>
									<Grid
										container
										direction="column"
										sx={{
											':hover img.cover': {
												transform: 'scale(1.05)',
											},
										}}
									>
										<Grid
											borderRadius={2}
											overflow="hidden"
											sx={{
												'& img': {
													width: '100%',
													height: 'auto',
													transition: 'transform 0.3s ease',
												},
											}}
										>
											<Image className="cover" src={item._embedded['wp:featuredmedia'][0].source_url} alt={item.title.rendered} width={447} height={238} />
										</Grid>
										<Grid container direction="column" paddingY={3}>
											<Typography fontSize={16} color="grey.800" marginBottom="4px">
												{filteredList.map(({ label }) => label).join(', ')}
											</Typography>
											<Typography fontSize={22} fontWeight={500}>
												<span dangerouslySetInnerHTML={{ __html: item.title.rendered }} />
											</Typography>
										</Grid>
										<Grid
											container
											alignItems="center"
											sx={{
												'& img': {
													width: 32,
													borderRadius: 99,
													boxShadow: '0 8px 10px #0000001A',
												},
											}}
										>
											<Image src={Logo} alt="logo" width={32} height={32} />
											<Typography fontSize={16} marginLeft={1}>
												엔라이튼
											</Typography>
										</Grid>
									</Grid>
								</Link>
							</Grid>
						);
					})}
					<Grid />
				</Slider>
			</Grid>
		</>
	);
}
